/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {translate} from '@ion/core/src/locale/locale';
import {
  Account,
  CTCEntityResponseWrapper,
  Device,
  EntityStatus,
  GenericObject
} from '@ion/core/src/types/entities';
import {TemplateColumn} from '@ion/core/src/types/templates';
import {toDateISOString} from '@ion/core/src/utils/DateUtil';
import {convertStatus} from '@ion/dls/src/utils/ComponentUtils';
import {validateDevices} from '../../helpers';
import {importDevices, importDevicesValidation} from '../../store/actions/importer';
import {toggleUserPrompt} from '../../store/actions/user-prompt';

// defines the prop definitions for import start ...
export interface ImportStartType {
  items: Device[] | GenericObject;
  account?: Account;
  dispatch?: Function;
}

/**
 * Import process starts after validated Excel
 */
export function onImportStart({
  items,
  dispatch
}: ImportStartType) {
  const devices = augmentItems(items);
  if (items?.length) {
    const {valid, invalid} = validateDevices(devices);
    if (valid?.length && !invalid?.length) {
      dispatch(importDevices(devices));
    } else {
      dispatch(importDevicesValidation(true));
    }
  } else {
    dispatch(toggleUserPrompt({message: translate('admin-devices:import.errors.emptyDevices')}));
  }
}

const formatTemplateFields = (item: Device, templateKeys: string[]) => {
  const keys = {};
  templateKeys.forEach(key => (keys[key] = true));
  Object.entries(item).forEach(([field, value]) => {
    if (!keys[field]) {
      let val = value?.toString();
      if (field === 'esn' || field === 'id') {
        val = val || null;
      }
      item[field] = val;
    }
  });
};

/**
 * Augments the imported items with additional inferred Device attributes
 * required by CTC Services.
 */
const augmentItems = (items: Device[] | GenericObject): CTCEntityResponseWrapper<Device>[] => {
  const devices                           = [];
  const templateColumns: TemplateColumn[] = translate('admin-devices:template', {returnObjects: true});
  const templateKeys                      = templateColumns.map(item => item.key);

  items.forEach((item) => {
    const {
      name, esn, status, shipDate, iccid, isdn, meid, imei, imei2, imsi, mdn, serialNumber, externalId, ipAddress
    } = item;
    item.name         = name.toString();
    item.esn          = esn?.toString() || null;
    item.iccid        = iccid?.toString();
    item.isdn         = isdn?.toString();
    item.meid         = meid?.toString();
    item.imei         = imei?.toString();
    item.imei2        = imei2?.toString();
    item.imsi         = imsi?.toString();
    item.mdn          = mdn?.toString();
    item.externalId   = externalId?.toString();
    item.ipAddress    = ipAddress?.toString();
    item.serialNumber = serialNumber?.toString();
    item.status       = convertStatus(status);
    item.shipDate     = toDateISOString(shipDate);

    item.id     ||= null;
    item.status ||= EntityStatus.Enabled;

    formatTemplateFields(item, templateKeys);
    devices.push({device: item});
  });
  return devices;
};
